import React, { Dispatch, SetStateAction, useCallback, useEffect } from "react"

import {
  ConsoleLogger,
  DefaultDeviceController,
  LogLevel,
  PermissionDeniedError,
} from "amazon-chime-sdk-js"

export type VideoPreviewProps = {
  setCameraMessage: Dispatch<SetStateAction<string>>
  setMicMessage: Dispatch<SetStateAction<string>>
  setSetupComplete: Dispatch<SetStateAction<boolean>>
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  setCameraMessage,
  setMicMessage,
  setSetupComplete,
}) => {
  const setupPreview = useCallback(async () => {
    const logger = new ConsoleLogger("MyLogger", LogLevel.WARN)
    const deviceController = new DefaultDeviceController(logger)

    const videoDevices = await deviceController.listVideoInputDevices()

    const videoInputDeviceInfo = videoDevices[0]
    if (!videoInputDeviceInfo) {
      setCameraMessage("No camera found")
    } else {
      if (!videoInputDeviceInfo.deviceId) {
        setCameraMessage("Camera is blocked")
      } else {
        try {
          await deviceController.startVideoInput(videoInputDeviceInfo.deviceId)
          const videoElement = document.getElementById(
            "video-preview"
          ) as HTMLVideoElement
          deviceController.startVideoPreviewForVideoInput(videoElement)
        } catch (e) {
          if (e instanceof PermissionDeniedError) {
            setCameraMessage("Camera is blocked")
          } else setCameraMessage("Unknown camera error")
        }
      }
    }

    const audioDevices = await deviceController.listAudioInputDevices()

    const audioInputDeviceInfo =
      audioDevices.find(device => device.deviceId === "default") ||
      audioDevices[0]

    if (!audioInputDeviceInfo) {
      setMicMessage("No microphone found")
    } else {
      if (!audioInputDeviceInfo.deviceId) {
        setMicMessage("Microphone is blocked")
      } else {
        try {
          await deviceController.startAudioInput(audioInputDeviceInfo.deviceId)
        } catch (e) {
          if (e instanceof PermissionDeniedError) {
            setMicMessage("Microphone is blocked")
          } else setMicMessage("Unknown microphone error")
        }
      }
    }
    setSetupComplete(true)

    return () => {
      deviceController.stopVideoInput()
      deviceController.stopAudioInput()
    }
  }, [setCameraMessage, setMicMessage, setSetupComplete])

  useEffect(() => {
    let cleanup: (() => void) | undefined

    const run = async () => {
      cleanup = await setupPreview()
    }
    run()

    return () => {
      if (cleanup) cleanup()
    }
  }, [setupPreview])

  return (
    <video
      id="video-preview"
      className="my-4 aspect-video max-w-full rounded-lg object-contain"
    />
  )
}

export default VideoPreview
